import { useState } from "react";
import { CheckIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { Button, Card, ConfirmModal, Input, Textarea } from "../../ui";
import { CommentText } from "../review/ReviewHistory";
import { formatDateTime, formatScore } from "../review/model";

export const marksOf = (evaluation) => Object.fromEntries((evaluation?.marks ?? []).map((m) => [m.criterionId, { marks: String(m.marks), comment: m.comment ?? "" }]));
export const isMark = (value) => value !== "" && value != null && Number(value) >= 0 && Number(value) <= 100;
const bandOf = (criterion, marks) => (isMark(marks) ? criterion.bands?.find((b) => Number(marks) >= b.minMarks && Number(marks) <= b.maxMarks)?.label : null);

export const hasInvalidMarks = (rubric, marks) => rubric.some((c) => marks[c.id]?.marks && !isMark(marks[c.id].marks));

// The draft the API saves: the criteria marked so far, with their comments
export const marksBodyOf = (rubric, marks) => ({
  marks: rubric
    .filter((c) => isMark(marks[c.id]?.marks))
    .map((c) => ({ criterionId: c.id, marks: Number(marks[c.id].marks), ...(marks[c.id].comment?.trim() && { comment: marks[c.id].comment.trim() }) })),
});

// The criteria a failed submit names as unmarked
export const unmarkedOf = (problem) => (problem?.code === "incomplete-marks" ? problem.errors.map((e) => e.field?.replace("criterion:", "")) : []);

const SUBMIT_NOTE = "Your marks and comments are then kept permanently: you cannot change them unless HEC sends them back, but you can add a correction to a comment.";

/**
 * The evaluator's marks against a rubric, saved as a draft until submitted. While re-marking,
 * `previous` is the round HEC sent back, shown beside each criterion.
 */
export function MarksCard({ rubric, marks, evaluation, changed, missing = [], busy, previous, onChange, onSave, onSubmit, submitNote = SUBMIT_NOTE }) {
  const [confirming, setConfirming] = useState(false);
  const submitted = evaluation?.state === "SUBMITTED";
  const total = rubric.reduce((sum, c) => sum + (isMark(marks[c.id]?.marks) ? (Number(marks[c.id].marks) * c.weight) / 100 : 0), 0);
  const markedCount = rubric.filter((c) => isMark(marks[c.id]?.marks)).length;
  const previousOf = (criterionId) => previous?.marks.find((m) => m.criterionId === criterionId);

  return (
    <Card
      title="Your marks"
      subtitle={submitted ? `Submitted ${formatDateTime(evaluation.submittedAt)}` : null}
      actions={
        <span className="rounded-md bg-brand/10 px-3 py-1 text-sm font-semibold text-brand">
          Weighted total {formatScore(submitted ? evaluation.weightedTotal : total)} / 100
        </span>
      }
    >
      <ol className="flex flex-col gap-4">
        {rubric.map((c, i) => (
          <li key={c.id} className={`grid gap-3 rounded-lg border p-4 md:grid-cols-[1fr_9rem] ${missing.includes(c.id) ? "border-red-300 bg-red-50" : "border-slate-200"}`}>
            <div>
              <p className="font-semibold text-slate-900">
                {i + 1}. {c.name} <span className="font-normal text-slate-500">· weight {c.weight}%</span>
              </p>
              {c.guideline && <p className="text-sm text-slate-600">{c.guideline}</p>}
              {c.bands?.length > 0 && (
                <p className="mt-1 text-xs text-slate-500">{c.bands.map((b) => `${b.label} ${b.minMarks}–${b.maxMarks}`).join(" · ")}</p>
              )}
              {previousOf(c.id) && (
                <div className="mt-2 rounded-md bg-slate-50 p-2">
                  <p className="text-xs font-semibold text-slate-700">
                    Round {previous.roundNo}: {formatScore(previousOf(c.id).marks)} / 100
                  </p>
                  {previousOf(c.id).comment ? <CommentText comment={previousOf(c.id).comment} /> : <p className="text-xs text-slate-600">No comment</p>}
                </div>
              )}
            </div>
            <Input
              type="number"
              aria-label={`Marks for ${c.name}`}
              min={0}
              max={100}
              step="0.5"
              suffix="/ 100"
              value={marks[c.id]?.marks ?? ""}
              onChange={(e) => onChange(c.id, { marks: e.target.value })}
              info={bandOf(c, marks[c.id]?.marks) ?? undefined}
              error={missing.includes(c.id) ? "Required" : marks[c.id]?.marks && !isMark(marks[c.id].marks) ? "0 to 100" : undefined}
              disabled={submitted || busy}
            />
            <Textarea
              className="md:col-span-2"
              aria-label={`Comment on ${c.name}`}
              placeholder="Comment (optional)"
              rows={1}
              maxLength={1000}
              value={marks[c.id]?.comment ?? ""}
              onChange={(e) => onChange(c.id, { comment: e.target.value })}
              disabled={submitted || busy}
            />
          </li>
        ))}
      </ol>
      {!submitted && (
        <div className="mt-5 flex flex-col-reverse items-center justify-between gap-3 sm:flex-row">
          <span className="text-sm text-slate-600">
            {markedCount} of {rubric.length} criteria marked{changed && <span className="ml-2 text-amber-700">Unsaved changes</span>}
          </span>
          <div className="flex gap-3">
            <Button variant="outline" icon={<CheckIcon className="h-4 w-4" />} onClick={() => onSave()} loading={busy && changed} disabled={!changed}>
              Save draft
            </Button>
            <Button icon={<PaperAirplaneIcon className="h-4 w-4" />} onClick={() => setConfirming(true)} disabled={busy || markedCount < rubric.length}>
              Submit marks
            </Button>
          </div>
        </div>
      )}
      <ConfirmModal
        open={confirming}
        onClose={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          onSubmit();
        }}
        loading={busy}
        title="Submit your marks?"
        description={`Weighted total ${formatScore(total)} / 100. ${submitNote}`}
        confirmLabel="Submit"
      />
    </Card>
  );
}
